import { minutesToMs } from '@thyme/libs/src/time/convert'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { CanaryStatus, loadVersionMetadata } from './version'

const DEFAULT_CHECK_INTERVAL_MINUTES = 5
const DEFAULT_CHECK_INTERVAL_MS = minutesToMs(DEFAULT_CHECK_INTERVAL_MINUTES)

/**
 * A Pinia store for checking if a new version of the app is available. It does this
 * by making a call on a configurable interval to check the latest deployed version
 * of the app.
 * This is intended for use with SPA deployments (not chrome extension).
 *
 * Usage:
 * ```ts
 * import { createAppVersionCheckStore } from '@thyme/libs/src/version/checkNewVersion'
 *
 * // Do this once in your app to define the store. APP_VERSION is whatever variable holds
 * // the value of this code version.
 * export const useAppVersionChecker = createAppVersionCheckStore(APP_VERSION)
 *
 * // In your component
 * const { isNewVersionAvailable } = storeToRefs(useAppVersionChecker())
 * ```
 * @param thisDeploymentVersion - The version of the currently running code
 * @param thisDeploymentCanaryStatus - The canary status when this deployment was loaded
 * @param checkIntervalMs - How often to check for new versions
 */
export function createAppVersionCheckStore(
  thisDeploymentVersion: string,
  thisDeploymentCanaryStatus: CanaryStatus,
  checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS
) {
  return defineStore('appVersionState', () => {
    const isNewVersionAvailable = ref(false)

    /**
     * Checks if a new version is available. Only shows the banner when:
     * 1. The current deployment (running in the browser) was from the primary deployment
     * 2. The new version being fetched is from the primary deployment
     * 3. The versions differ
     *
     * This prevents the banner from showing when a canary is being rolled out.
     * The banner only appears when there's a new primary version.
     */
    async function checkForNewVersion() {
      const { version: latestVersion, canaryStatus: latestCanaryStatus } =
        await loadVersionMetadata()

      if (!latestVersion) {
        return
      }

      // Only show banner if:
      // - Current deployment was from primary
      // - Latest version is from primary
      // - They differ
      if (
        thisDeploymentCanaryStatus === CanaryStatus.Primary &&
        latestCanaryStatus === CanaryStatus.Primary &&
        latestVersion !== thisDeploymentVersion
      ) {
        console.debug(
          'New primary version available:',
          latestVersion,
          '!=',
          thisDeploymentVersion
        )
        isNewVersionAvailable.value = true
      } else {
        isNewVersionAvailable.value = false
      }
    }

    setInterval(() => {
      void checkForNewVersion()
    }, checkIntervalMs)
    void checkForNewVersion()

    return {
      isNewVersionAvailable,
    }
  })
}
